import { IonModal } from "@ionic/react";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { jsPDF } from "jspdf";
import { useConfig } from "../../contexts/ConfigContext";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import DisplayContent from "../../components/molecules/DisplayContent";
import { getStudyContent } from "../../services/study.service";
import { StudyContentType } from "../../types/welcome";

interface Props {
  isOpen: boolean;
  onDismiss(): void;
}

const stripHtml = (value: unknown): string => {
  if (typeof value !== "string") return "";
  const container = document.createElement("div");
  container.innerHTML = value;
  return (container.textContent || container.innerText || "").trim();
};

const extractListItems = (items: any[]): string[] => {
  return items.flatMap((item) => {
    if (typeof item === "string") return [stripHtml(item)];
    if (item && typeof item === "object") {
      const content = stripHtml(item.content);
      const nested = Array.isArray(item.items) ? extractListItems(item.items) : [];
      return [content, ...nested].filter(Boolean);
    }
    return [];
  });
};

const editorJsToParagraphs = (content?: StudyContentType["content"]): string[] => {
  if (!content?.blocks?.length) return [];

  const paragraphs: string[] = [];

  content.blocks.forEach((block) => {
    switch (block.type) {
      case "header":
      case "paragraph": {
        const text = stripHtml(block.data?.text);
        if (text) paragraphs.push(text);
        break;
      }
      case "list": {
        const items = Array.isArray(block.data?.items)
          ? extractListItems(block.data.items)
          : [];
        items.forEach((item) => paragraphs.push(`- ${item}`));
        break;
      }
      case "table": {
        const rows = Array.isArray(block.data?.content) ? block.data.content : [];
        rows.forEach((row: unknown) => {
          if (!Array.isArray(row)) return;
          const text = row.map((cell) => stripHtml(cell)).filter(Boolean).join(" | ");
          if (text) paragraphs.push(text);
        });
        break;
      }
      case "delimiter": {
        paragraphs.push("--------------------");
        break;
      }
      default: {
        const fallback = stripHtml(block.data?.text);
        if (fallback) paragraphs.push(fallback);
      }
    }
  });

  return paragraphs;
};

const LegalMentionModal: FC<Props> = ({ isOpen, onDismiss }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { study, selectedRole } = useConfig();

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [contentData, setContentData] = useState<StudyContentType | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const loadConsentContent = async () => {
      if (!isOpen) return;

      const studyId = study?.id;
      const consentId = selectedRole?.consent?.id;

      if (!studyId || !consentId) {
        setContentData(null);
        setIsError(true);
        return;
      }

      setIsLoading(true);
      setIsError(false);

      try {
        const { data } = await getStudyContent({
          studyId,
          contentId: consentId,
          lang: language,
        });

        if (!isCancelled) {
          setContentData(data);
        }
      } catch (_error) {
        if (!isCancelled) {
          setContentData(null);
          setIsError(true);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadConsentContent();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, language, selectedRole?.consent?.id, study?.id]);

  const handleDownloadPdf = () => {
    if (!contentData?.content || isDownloadingPdf) return;

    const paragraphs = editorJsToParagraphs(contentData.content);
    if (!paragraphs.length) return;

    setIsDownloadingPdf(true);

    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const margin = 40;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const maxWidth = pageWidth - margin * 2;
      const lineHeight = 16;
      let y = margin;

      const title = t("profile_information_note");
      doc.setFontSize(16);
      const titleLines = doc.splitTextToSize(title, maxWidth);
      titleLines.forEach((line: string) => {
        if (y > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, margin, y);
        y += lineHeight;
      });

      y += 8;
      doc.setFontSize(11);

      paragraphs.forEach((paragraph) => {
        const lines = doc.splitTextToSize(paragraph, maxWidth);
        lines.forEach((line: string) => {
          if (y > pageHeight - margin) {
            doc.addPage();
            y = margin;
          }
          doc.text(line, margin, y);
          y += lineHeight;
        });
        y += 8;
      });

      const noteTitle = t("profile_information_note") || "information-note";
      const safeBaseName = noteTitle
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9-_ ]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .toLowerCase();
      const datePart = new Date().toISOString().slice(0, 10);

      doc.save(`${safeBaseName || "information-note"}-${datePart}.pdf`);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <IonModal
      className="resonance-modal"
      isOpen={isOpen}
      backdropDismiss={true}
      onWillDismiss={onDismiss}
    >
      <Container onClick={onDismiss}>
        <Card onClick={(e) => e.stopPropagation()}>
          <Title>{t("profile_information_note")}</Title>

          <ContentArea>
            {isLoading && <ResonanceLoader />}
            {!isLoading && isError && (
              <Message>{t("common_server_error_desc")}</Message>
            )}
            {!isLoading && !isError && contentData?.content && (
              <DisplayContent editorJsData={contentData.content} />
            )}
            {!isLoading && !isError && !contentData?.content && (
              <Message>{t("home_content_none_text")}</Message>
            )}
          </ContentArea>

          <Buttons>
            <ModalButton
              as="button"
              type="button"
              onClick={handleDownloadPdf}
              disabled={
                isLoading ||
                isError ||
                !contentData?.content ||
                isDownloadingPdf
              }
            >
              {isDownloadingPdf
                ? t("profile_download_pdf_loading")
                : t("profile_download_pdf")}
            </ModalButton>
            <ModalButton type="button" onClick={onDismiss}>
              {t("common_close")}
            </ModalButton>
          </Buttons>
        </Card>
      </Container>
    </IonModal>
  );
};

const Container = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  width: 100%;
  max-width: 900px;
  max-height: 85vh;
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
`;

const Message = styled.div`
  padding: 10px 0;
  font-size: 14px;
  text-align: center;
`;

const ContentArea = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  background-color: white;
  color: ${(props) => props.theme.primary.main};
  font-weight: bold;
  cursor: pointer;
  border: 1px solid ${(props) => props.theme.primary.main};
  border-radius: 5px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export default LegalMentionModal;
